import { Injectable } from "@angular/core";
import { ObgConfigFeAdaptiveClientCommon } from "@obg/common.config";
import {
	ActionBannerPrimeField,
	HomePageGameCollection,
	MainBannerConfig,
	RegistrationStart,
	SponsorshipBannerConfig,
} from "@obg/common.config/src/common/home-page";
import { ComponentOrder } from "@obg/common.home";
import { ImageContent } from "@obg/ngx.core.content";
import { ConfigService, FeatureFlagService, ignoreNil } from "@obg/ngx.global";
import { Observable } from "rxjs";
import { map, take } from "rxjs/operators";

import { MFeFooterFlag } from "./home-wrapper.model";

export const MFE_FOOTER_FLAG_KEY = "mfe_footer";

@Injectable({
	providedIn: "root",
})
export class HomeConfigService {
	constructor(
		private service: ConfigService<ObgConfigFeAdaptiveClientCommon>,
		private featureFlagService: FeatureFlagService
	) {}

	getHomePageV2Enabled(): boolean {
		return this.service.get((config) => config.commonUi.home.homePageV2?.isEnabled, false);
	}

	getMobileViewEnabled(): boolean {
		return this.service.get((config) => config.commonUi.home?.isMobileView, false);
	}

	getExternalPromotionsLobbyUrlKey() {
		return this.service.get((config) => config.commonUi.home.homePageV2.externalPromotionsLobbyUrlKey, undefined);
	}

	getComponentOrder(): ComponentOrder {
		return this.service.get((config) => config.commonUi.home.homePageV2?.componentOrder, { defaultMarket: [] });
	}

	getShowCorrectedRealmIcons(): boolean {
		return this.service.get((config) => config.commonUi.home.homePageV2?.alternatePaymentIconsStyle, false);
	}

	getIsSearchOnHomePageEnabled(): boolean {
		return this.service.get((config) => config.commonUi.home?.isSearchOnHomePageEnabled, false);
	}

	getFallbackImage(): ImageContent {
		return this.service.get((config) => config.commonUi.home.homePageV2?.promotionLobby?.fallbackImage, {
			url: "",
		});
	}

	getSponsorshipBannersState(): SponsorshipBannerConfig[] {
		return this.service.get((config) => config.commonUi.home.homePageV2.sponsorshipBanners, []);
	}

	getSponsorshipBannerSpecificMarket(banner: SponsorshipBannerConfig): string[] {
		return banner.specificMarket || [];
	}

	getMainBanner(): MainBannerConfig {
		return this.service.get((config) => config.commonUi.home.homePageV2?.mainBanner, {
			isHidden: false,
			specificMarket: [],
		});
	}

	getIsTermsAndConditionsEnabled() {
		return this.service.get(
			(config) => config.commonUi.home.homePageV2?.promotionSection?.isTermsAndConditionsEnabled,
			false
		);
	}

	getRegistrationStart(): RegistrationStart | undefined {
		return this.service.get((config) => config.commonUi.home.homePageV2.registrationStart, undefined);
	}

	getActionBannerPrimeField() {
		return this.service.get(
			(config) => config.commonUi.home.homePageV2.actionBanner?.primeField,
			ActionBannerPrimeField.email
		);
	}

	getEnabledWebComponents(): string[] {
		return this.service.get((config) => config.commonUi.home.homePageV2.enabledWebComponents, []);
	}

	getGameCollectionNames(): Record<string, HomePageGameCollection> | undefined {
		return this.service.get(
			(config) => config.commonUi.home.homePageV2?.gamingPreviewSection?.gameCollections,
			undefined
		);
	}

	getGameParentCollectionName(): string | undefined {
		return this.service.get(
			(config) => config.commonUi.home.homePageV2?.gamingPreviewSection?.parentCollectionName,
			undefined
		);
	}

	getHideRegistrationBanner() {
		return this.service.get(
			(config) => config.commonUi.home.homePageV2.actionBanner?.hideRegistrationBanner,
			false
		);
	}

	getShowMainBannerForLoggedOutUser() {
		return this.service.get((config) => config.commonUi.home.homePageV2?.showMainBannerForLoggedOutUser, false);
	}

	getIsHomePageFabricEnabled(): boolean {
		return this.service.get(
			(config) => config.commonUi.home.homePageV2.homePageFabric?.isHomePageFabricEnabled,
			false
		);
	}

	getMFeFooterEnabled$(): Observable<boolean> {
		return this.featureFlagService.get$<MFeFooterFlag | undefined>(MFE_FOOTER_FLAG_KEY).pipe(
			ignoreNil(),
			take(1),
			map((footerFlag) => footerFlag?.enabled)
		);
	}

	isCarouselBannerEnabled(): boolean {
		return this.service.get((config) => config.commonUi.home.homePageV2?.isCarouselBannerEnabled, false);
	}
}
